/**
 * Weather fetching utility using Open-Meteo API (free, no API key required)
 * Provides auto-weather functionality for daily log forms
 */

export interface WeatherData {
  temperature: number; // Fahrenheit
  conditions: string; // Mapped to our condition strings
  weatherCode: number; // Original WMO code for reference
  timestamp: string; // ISO timestamp
}

/**
 * WMO Weather Code to Kablan Condition mapping
 * Based on WMO Code Table 4677
 */
function mapWeatherCodeToCondition(code: number): string {
  // Clear sky
  if (code === 0) return "Clear";

  // Mainly clear / Partly cloudy
  if (code === 1 || code === 2) return "Partly Cloudy";

  // Overcast
  if (code === 3) return "Cloudy";

  // Fog
  if (code === 45 || code === 48) return "Fog";

  // Drizzle (light, moderate, dense)
  if ([51, 53, 55].includes(code)) return "Light Rain";

  // Rain (slight)
  if (code === 61) return "Light Rain";

  // Rain (moderate)
  if (code === 63) return "Rain";

  // Rain (heavy)
  if (code === 65) return "Heavy Rain";

  // Freezing rain/drizzle
  if ([56, 57, 66, 67].includes(code)) return "Light Rain";

  // Snow (all intensities)
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";

  // Thunderstorm (slight, moderate, with hail)
  if ([95, 96, 99].includes(code)) return "Thunderstorm";

  // Rain showers (slight, moderate, violent)
  if (code === 80) return "Light Rain";
  if (code === 81) return "Rain";
  if (code === 82) return "Heavy Rain";

  // Default fallback
  return "Clear";
}

/**
 * Get user's current GPS coordinates
 * Uses browser geolocation with timeout and caching
 */
export async function getCurrentPosition(): Promise<{ latitude: number; longitude: number } | null> {
  if (!("geolocation" in navigator)) {
    console.warn("Geolocation not available in this browser");
    return null;
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 5000, // 5 second timeout
        maximumAge: 300000, // Cache for 5 minutes
        enableHighAccuracy: false, // Faster, less battery drain
      });
    });

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  } catch (error) {
    console.warn("Failed to get geolocation:", error);
    return null;
  }
}

/**
 * Fetch current weather from Open-Meteo API
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @returns Weather data or null if fetch fails
 */
export async function fetchWeather(
  latitude: number,
  longitude: number
): Promise<WeatherData | null> {
  try {
    const url = new URL("https://api.open-meteo.com/v1/forecast");
    url.searchParams.set("latitude", latitude.toFixed(4));
    url.searchParams.set("longitude", longitude.toFixed(4));
    url.searchParams.set("current", "temperature_2m,weather_code");
    url.searchParams.set("temperature_unit", "fahrenheit");
    url.searchParams.set("timezone", "auto");

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      console.warn("Weather API returned error:", response.status);
      return null;
    }

    const data = await response.json();

    if (!data.current || data.current.temperature_2m === undefined) {
      console.warn("Weather API response missing current data");
      return null;
    }

    return {
      temperature: Math.round(data.current.temperature_2m),
      conditions: mapWeatherCodeToCondition(data.current.weather_code ?? 0),
      weatherCode: data.current.weather_code ?? 0,
      timestamp: data.current.time || new Date().toISOString(),
    };
  } catch (error) {
    console.warn("Failed to fetch weather:", error);
    return null;
  }
}

/**
 * Get current weather based on user's GPS location
 * Combines geolocation and weather fetching
 * @returns Weather data or null if unavailable
 */
export async function getAutoWeather(): Promise<WeatherData | null> {
  const position = await getCurrentPosition();

  if (!position) {
    return null;
  }

  return fetchWeather(position.latitude, position.longitude);
}
